import { useEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearch } from '@tanstack/react-router'
import { usePagedList, type UsePagedListResult } from '../lib/use-paged-list'
import { PaginationControls } from '../components/lists/PaginationControls'
import { format, parseISO } from 'date-fns'
import { addBusinessDays } from 'date-fns'
import { ApiError } from '../api/client'
import {
  cancelWfhRequest,
  createWfhRequest,
  decideWfhRequest,
  getWfhApprovalsSummary,
  getWfhDocumentUrl,
  listMyWfhRequests,
  listPendingWfhRequests,
  listReviewedWfhRequests,
  uploadWfhDocument,
  type BusinessCategory,
  type WfhDocRef,
  type WfhPendingApproval,
  type WfhRequest,
  type WfhRequestStatus,
  type WfhRequestType,
  type WfhTimingFlag,
} from '../api/wfh'
import {
  StagedDocumentPicker,
  type StagedFile,
} from '../components/employee/StagedDocumentPicker'
import { Badge, type BadgeVariant } from '../components/primitives/Badge'
import { Button } from '../components/primitives/Button'
import { Card } from '../components/primitives/Card'
import { Icon, type IconName } from '../components/primitives/Icon'
import { formatBytes } from '../components/primitives/FileDropzone'
import {
  HalfDayPicker,
  listWorkingDays,
  type HalfDayMap,
} from '../components/employee/HalfDayPicker'
import { RangeCalendar } from '../components/employee/RangeCalendar'
import { FadeIn } from '../components/primitives/FadeIn'
import { Field } from '../components/primitives/Field'
import { Modal } from '../components/primitives/Modal'
import { PageHead } from '../components/primitives/PageHead'
import { ThinkingDots } from '../components/primitives/ThinkingDots'
import { useToast } from '../components/primitives/Toast'
import { useSession } from '../auth/auth-context'
import { useViewport } from '../lib/use-viewport'
import { roleAtLeast, type Role } from '../lib/role'
import { fmtDateWithWeekday } from '../lib/format'

const MINE_KEY = ['wfh', 'mine'] as const
const PENDING_KEY = ['wfh', 'pending'] as const
const REVIEWED_KEY = ['wfh', 'reviewed'] as const

const STATUS_BADGE: Record<WfhRequestStatus, { variant: BadgeVariant; label: string }> = {
  pending: { variant: 'warning', label: 'Pending' },
  approved: { variant: 'success', label: 'Approved' },
  rejected: { variant: 'danger', label: 'Rejected' },
  cancelled: { variant: 'default', label: 'Cancelled' },
  logged: { variant: 'info', label: 'Logged' },
}

const TYPE_LABEL: Record<WfhRequestType, string> = {
  wfh: 'Work from home',
  business: 'Out on business',
}

const TIMING_FLAG_LABEL: Record<NonNullable<WfhTimingFlag>, string> = {
  same_day: 'Same-day',
  retrospective: 'Retrospective',
  logged_late: 'Logged late',
}

const BUSINESS_CATEGORIES: BusinessCategory[] = [
  'Client or external meeting',
  'Conference or event',
  'Training or course',
  'Site visit',
  'Other',
]

/** The prototype's timing chip: amber outline pill. */
function FlagChip({ flag }: { flag: WfhTimingFlag }): JSX.Element | null {
  if (!flag) return null
  return (
    <span
      style={{
        fontSize: 11.5,
        fontWeight: 600,
        color: 'var(--warning-fg)',
        border: '1px solid var(--warning-line)',
        borderRadius: 999,
        padding: '1px 8px',
      }}
    >
      {TIMING_FLAG_LABEL[flag]}
    </span>
  )
}

// ── Shared style tokens (kept in sync with ApplyLeavePage) ──────────────────
const mutedText: CSSProperties = { margin: 0, fontSize: 13, color: 'var(--text-2)' }
// Section heading rendered ABOVE a card (outside it) — muted grey.
const sectionLabel: CSSProperties = {
  margin: 0,
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: 'var(--text-3)',
}
const listStyle: CSSProperties = { listStyle: 'none', margin: 0, padding: 0 }
// Rows are a divided list inside one card (no per-row box) — a divider on every
// row after the first.
function rowStyle(first: boolean): CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '9px 0',
    borderTop: first ? 'none' : '1px solid var(--border)',
  }
}
const textareaStyle: CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: 10,
  border: '1px solid var(--border)',
  borderRadius: 8,
  background: 'var(--surface)',
  resize: 'vertical',
  fontFamily: 'inherit',
  fontSize: 13,
  color: 'var(--text)',
}
// Tab bar styling copied from the shared TabbedHub so /wfh reads the same.
const tabListStyle: CSSProperties = {
  display: 'flex',
  gap: 18,
  flexWrap: 'wrap',
  borderBottom: '1px solid var(--border)',
}
const tabButtonStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '4px 0 8px',
  fontSize: 14,
  fontWeight: 500,
  fontFamily: 'inherit',
  background: 'none',
  border: 'none',
  borderBottom: '2px solid transparent',
  marginBottom: -1,
  outline: 'none',
  boxShadow: 'none',
  cursor: 'pointer',
  color: 'var(--text-3)',
  transition: 'color 120ms ease, border-color 120ms ease',
}
const tabButtonActiveStyle: CSSProperties = {
  color: 'var(--steel-blue)',
  fontWeight: 700,
  borderBottom: '2px solid var(--steel-blue)',
}

type WfhTab = 'request' | 'mine' | 'approvals'

function errMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return 'Something went wrong'
}

function shortDate(iso: string): string {
  return format(parseISO(iso), 'd MMM yyyy')
}

/** Live summary shown on the collapsed dates trigger. */
function datesSummary(from: string, to: string): string | null {
  if (from === '' || to === '') return null
  return from === to ? shortDate(from) : `${shortDate(from)} – ${shortDate(to)}`
}

/** Working days in the request minus a half for each half-day. */
function requestDays(r: WfhRequest): number {
  const wd = listWorkingDays(r.startDate, r.endDate, [])
  const halves = wd.filter((d) => r.halfDays.some((h) => h.date === d)).length
  return wd.length - 0.5 * halves
}

function daysText(n: number): string {
  return `${Number.isInteger(n) ? n : n.toFixed(1)} ${n === 1 ? 'day' : 'days'}`
}

interface DocPreview {
  reqId: string
  doc: WfhDocRef
}

/** Attachment chips for a request; clicking one opens the preview modal. */
function DocLinks({
  req,
  onPreview,
}: {
  req: WfhRequest
  onPreview: (p: DocPreview) => void
}): JSX.Element | null {
  if (req.documents.length === 0) return null
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 4 }}>
      {req.documents.map((d) => (
        <button
          key={d.id}
          type="button"
          onClick={() => onPreview({ reqId: req.id, doc: d })}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 3,
            fontSize: 12,
            color: 'var(--steel-blue)',
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          <Icon name="attach_file" size={13} aria-hidden="true" />
          {d.filename}
        </button>
      ))}
    </div>
  )
}

/** Inline preview body for the modal — image via <img>, everything else via
 *  <iframe> (browser's built-in viewer). Bytes stream through the api pod;
 *  the S3 URL never reaches the browser. Mirrors LeaveDetailPage. */
function WfhDocumentPreview({ preview }: { preview: DocPreview }): JSX.Element {
  const url = getWfhDocumentUrl(preview.reqId, preview.doc.id)
  const isImage = preview.doc.mimeType.startsWith('image/')
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div
        style={{
          width: '100%',
          height: 'min(70vh, 720px)',
          background: 'var(--surface-2)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {isImage ? (
          <img
            src={url}
            alt={preview.doc.filename}
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
          />
        ) : (
          <iframe
            src={url}
            title={preview.doc.filename}
            style={{ width: '100%', height: '100%', border: 'none' }}
          />
        )}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 12,
          color: 'var(--text-3)',
        }}
      >
        <span>{preview.doc.mimeType}</span>
        <span aria-hidden="true">·</span>
        <span>{formatBytes(preview.doc.byteSize)}</span>
        <span style={{ flex: 1 }} />
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            color: 'var(--steel-blue)',
            textDecoration: 'none',
            fontWeight: 600,
          }}
        >
          <Icon name="open_in_new" size={14} aria-hidden="true" />
          Open in new tab
        </a>
      </div>
    </div>
  )
}

/** Numbered pagination footer for a WFH list — hidden when the list is empty. */
function Pager<T>({ list }: { list: UsePagedListResult<T> }): JSX.Element | null {
  if (list.total === 0) return null
  return (
    <PaginationControls
      page={list.page}
      pageSize={list.pageSize}
      total={list.total}
      totalPages={list.totalPages}
      pageSizeOptions={list.pageSizeOptions}
      rangeStart={list.rangeStart}
      rangeEnd={list.rangeEnd}
      canPrev={list.canPrev}
      canNext={list.canNext}
      onPageChange={list.setPage}
      onPageSizeChange={list.setPageSize}
      isFetching={list.isFetching}
      standalone
    />
  )
}

/** Centered empty state with a muted glyph. */
function EmptyState({ icon, text }: { icon: IconName; text: string }): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 10,
        padding: '28px 4px',
        textAlign: 'center',
      }}
    >
      <Icon name={icon} size={30} color="var(--text-3)" aria-hidden="true" />
      <span style={{ fontSize: 13, color: 'var(--text-3)' }}>{text}</span>
    </div>
  )
}

/** Red pill count for the tab label, matching the leave page's TabCount. */
function TabCount({ n }: { n: number }): JSX.Element | null {
  if (n <= 0) return null
  return (
    <span
      style={{
        marginLeft: 6,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 18,
        height: 18,
        padding: '0 5px',
        borderRadius: 999,
        background: 'var(--danger)',
        color: 'var(--surface)',
        fontSize: 11,
        fontWeight: 700,
        lineHeight: 1,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {n}
    </span>
  )
}

function dateRangeLabel(r: WfhRequest): string {
  const base =
    r.startDate === r.endDate
      ? fmtDateWithWeekday(r.startDate)
      : `${fmtDateWithWeekday(r.startDate)} → ${fmtDateWithWeekday(r.endDate)}`
  const halves = r.halfDays.length
  if (halves === 0) return base
  const first = r.halfDays[0]
  if (r.startDate === r.endDate && first != null) {
    return `${base} (${first.slot} half day)`
  }
  return `${base} (${halves} half ${halves === 1 ? 'day' : 'days'})`
}

export function WfhPage(): JSX.Element {
  const { user } = useSession()
  const { isDesktop } = useViewport()
  const isApprover = user != null && roleAtLeast(user.role as Role, 'manager')
  const qc = useQueryClient()
  const toast = useToast()

  // Deep link: /wfh?tab=approvals&focus=<requestId> — the Slack card's
  // "Open in LMS" lands on the approvals tab with the pending row highlighted.
  const search = useSearch({ strict: false }) as Record<string, string | undefined>
  const [tab, setTab] = useState<WfhTab>(
    search['tab'] === 'approvals' && isApprover
      ? 'approvals'
      : search['tab'] === 'mine'
        ? 'mine'
        : 'request',
  )
  const [highlightId] = useState<string | null>(search['focus'] ?? null)
  const focusRowRef = useRef<HTMLLIElement | null>(null)
  // Scroll the highlighted (deep-linked) pending row into view once the inbox
  // has loaded. The effect itself lives below pendingList's declaration.

  const today = format(new Date(), 'yyyy-MM-dd')
  // Prototype rule: backdate up to 5 working days.
  const minDate = format(addBusinessDays(new Date(), -5), 'yyyy-MM-dd')

  // ── Apply form ───────────────────────────────────────────────────────────
  // Two request types: wfh (needs approval) and business (notify-only).
  const [reqType, setReqType] = useState<WfhRequestType>('wfh')
  // RangeCalendar uses '' for "not picked" (matching ApplyLeavePage).
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [reason, setReason] = useState('')
  // Out-on-business fields.
  const [category, setCategory] = useState<BusinessCategory>('Client or external meeting')
  const [location, setLocation] = useState('')
  const [purpose, setPurpose] = useState('')
  // Part of day for a single-day request (prototype portion pills).
  const [portion, setPortion] = useState<'full' | 'am' | 'pm'>('full')
  // Per-date half days for a MULTI-day WFH request: `YYYY-MM-DD` → 'AM' | 'PM'.
  const [halfDays, setHalfDays] = useState<HalfDayMap>({})
  // Optional attachments, staged client-side and uploaded after the request is
  // created (the request has no draft to attach to before submit).
  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([])

  const isBusiness = reqType === 'business'
  const singleDay = from !== '' && from === to

  const hasRange = from !== '' && to !== '' && to >= from
  const businessValid = location.trim() !== '' && purpose.trim() !== ''
  const canSubmit =
    hasRange && from >= minDate && (isBusiness ? businessValid : reason.trim().length > 0)

  // Dates are picked in a click-to-open calendar behind a field trigger.
  const [datesOpen, setDatesOpen] = useState(false)
  // When reopening onto a committed range, the next click starts a FRESH pick
  // rather than extending the old range (matches ApplyLeavePage).
  const [datesReopenFreshPick, setDatesReopenFreshPick] = useState(false)
  const datesRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!datesOpen) return
    function onDocClick(e: MouseEvent): void {
      if (datesRef.current && !datesRef.current.contains(e.target as Node)) {
        setDatesOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [datesOpen])

  // Day count for the collapsed trigger — working days in range, minus a half
  // for each half-day (weekends/holidays already excluded by listWorkingDays).
  const workingDays = hasRange ? listWorkingDays(from, to, []) : []
  const halfCount = workingDays.filter((d) => halfDays[d] != null).length
  const singlePortionDays = singleDay && portion !== 'full' ? 0.5 : null
  const totalDays =
    singlePortionDays ?? workingDays.length - 0.5 * halfCount
  const daysLabel =
    totalDays > 0
      ? `${Number.isInteger(totalDays) ? totalDays : totalDays.toFixed(1)} ${totalDays === 1 ? 'day' : 'days'}`
      : null

  function pruneHalfDays(lo: string, hi: string): void {
    setHalfDays((prev) => {
      const next: Record<string, 'AM' | 'PM'> = {}
      for (const [date, slot] of Object.entries(prev)) {
        if (date >= lo && date <= hi) next[date] = slot
      }
      return next
    })
  }

  function handleRange(nextFrom: string, nextTo: string): void {
    // First click after reopening onto a committed range: start a fresh 1-day
    // pick at the clicked date rather than extending the old range. Keep the
    // calendar open so the user can still extend it or set AM/PM.
    if (datesReopenFreshPick) {
      setDatesReopenFreshPick(false)
      const clicked = nextTo !== '' ? nextTo : nextFrom
      setFrom(clicked)
      setTo(clicked)
      setHalfDays({})
      return
    }

    setFrom(nextFrom)
    setTo(nextTo)
    pruneHalfDays(nextFrom, nextTo)

    // Auto-close once the selection is complete, so no separate "Done" click is
    // needed: a second click on a LATER date (multi-day) OR a re-click on the
    // already-selected single day both finish the pick.
    const reclickSameDay =
      nextFrom !== '' && nextFrom === nextTo && from === nextFrom && to === nextTo
    const complete = Boolean(nextFrom && nextTo && nextTo > nextFrom) || reclickSameDay
    if (complete) setDatesOpen(false)
  }

  const createMut = useMutation({
    mutationFn: async () => {
      // Single-day requests use the portion pill; multi-day uses per-date halfs.
      const effectiveHalfDays: typeof halfDays = isBusiness
        ? {}
        : singleDay && portion !== 'full'
          ? { [from]: portion === 'am' ? 'AM' : 'PM' }
          : halfDays
      const created = await createWfhRequest({
        requestType: reqType,
        startDate: from,
        endDate: to,
        reason: isBusiness
          ? `${category} · ${location.trim()} · ${purpose.trim()}`
          : reason.trim(),
        ...(isBusiness
          ? { category, location: location.trim(), purpose: purpose.trim() }
          : {}),
        halfDays: isBusiness
          ? []
          : Object.entries(effectiveHalfDays)
              .filter(([date]) => date >= from && date <= to)
              .map(([date, slot]) => ({ date, slot })),
      })
      // Attachments are optional and uploaded after the request exists. Report
      // upload failures without failing the (already-created) request.
      let failed = 0
      for (const f of stagedFiles) {
        try {
          await uploadWfhDocument(created.id, f.file)
        } catch {
          failed += 1
        }
      }
      return { failed }
    },
    onSuccess: ({ failed }) => {
      const verb = isBusiness ? 'Out-on-business day logged' : 'WFH request submitted'
      toast.notify(
        failed > 0
          ? `${verb}, but ${failed} attachment(s) failed to upload`
          : verb,
        failed > 0 ? 'warning' : 'success',
      )
      setFrom('')
      setTo('')
      setReason('')
      setLocation('')
      setPurpose('')
      setPortion('full')
      setHalfDays({})
      setStagedFiles([])
      setDatesOpen(false)
      void qc.invalidateQueries({ queryKey: MINE_KEY })
      void qc.invalidateQueries({ queryKey: PENDING_KEY })
    },
    onError: (err) => toast.notify(errMessage(err), 'error'),
  })

  // ── My requests ──────────────────────────────────────────────────────────
  const mineList = usePagedList<WfhRequest>({
    queryKey: [...MINE_KEY],
    fetcher: ({ page, pageSize }) =>
      listMyWfhRequests({ page, limit: pageSize }).then((r) => ({
        rows: r.data,
        total: r.meta.total,
      })),
  })

  const cancelMut = useMutation({
    mutationFn: (id: string) => cancelWfhRequest(id),
    onSuccess: () => {
      toast.notify('Request cancelled', 'success')
      void qc.invalidateQueries({ queryKey: MINE_KEY })
      void qc.invalidateQueries({ queryKey: PENDING_KEY })
    },
    onError: (err) => toast.notify(errMessage(err), 'error'),
  })

  // ── Approver inbox ───────────────────────────────────────────────────────
  // Enabled whenever the user is an approver (not just on the active tab) so the
  // "For Approvals" tab badge stays accurate from any tab.
  const pendingList = usePagedList<WfhPendingApproval>({
    queryKey: [...PENDING_KEY],
    fetcher: ({ page, pageSize }) =>
      listPendingWfhRequests({ page, limit: pageSize }).then((r) => ({
        rows: r.data,
        total: r.meta.total,
      })),
    enabled: isApprover,
  })

  // "Your team today" strip + overdue count for the approvals tab.
  const approvalsSummary = useQuery({
    queryKey: ['wfh', 'approvals-summary'],
    queryFn: getWfhApprovalsSummary,
    enabled: isApprover && tab === 'approvals',
  })

  // Deep-linked row: once the pending inbox has loaded, scroll the highlighted
  // request into view so the approver lands right on it.
  useEffect(() => {
    if (!highlightId || pendingList.isLoading) return
    focusRowRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, [highlightId, pendingList.isLoading])

  // Approvers see their past decisions in the Reviewed section (always on,
  // matching the prototype's bottom card).
  const reviewedList = usePagedList<WfhRequest>({
    queryKey: [...REVIEWED_KEY],
    fetcher: ({ page, pageSize }) =>
      listReviewedWfhRequests({ page, limit: pageSize }).then((r) => ({
        rows: r.data,
        total: r.meta.total,
      })),
    enabled: isApprover && tab === 'approvals',
  })

  const [rejecting, setRejecting] = useState<WfhRequest | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  const [preview, setPreview] = useState<DocPreview | null>(null)

  const decideMut = useMutation({
    mutationFn: (args: { id: string; decision: 'approve' | 'reject'; rejectReason?: string }) =>
      decideWfhRequest(
        args.id,
        args.decision === 'reject'
          ? { decision: 'reject', rejectReason: args.rejectReason ?? '' }
          : { decision: 'approve' },
      ),
    onSuccess: (_data, args) => {
      toast.notify(args.decision === 'approve' ? 'Approved' : 'Rejected', 'success')
      setRejecting(null)
      setRejectReason('')
      void qc.invalidateQueries({ queryKey: PENDING_KEY })
      void qc.invalidateQueries({ queryKey: REVIEWED_KEY })
    },
    onError: (err) => toast.notify(errMessage(err), 'error'),
  })

  const myRows = mineList.rows
  // Prototype split: overdue (date passed, no decision) gets its own warning
  // card; Pending shows only upcoming requests.
  const overdueList = pendingList.rows.filter((p) => p.overdue)
  const currentPending = pendingList.rows.filter((p) => !p.overdue)
  const reviewedRows = reviewedList.rows

  return (
    <div className="page" data-testid="wfh-page">
      <PageHead
        title="Work location"
        subtitle="Log any working day away from the office. Nothing here uses your leave balance."
      />

      <div style={{ maxWidth: 820, width: '100%', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Pinned above the tabs so the policy is visible on every tab. */}
        <div
          role="note"
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            padding: '12px 16px',
            background: 'var(--warning-bg)',
            border: '1px solid var(--warning-line)',
            borderRadius: 'var(--r-md)',
          }}
        >
          <span
            aria-hidden
            style={{ fontSize: 14, lineHeight: 1, color: 'var(--warning-fg)', marginTop: 1, flexShrink: 0 }}
          >
            △
          </span>
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: '0.09em',
                textTransform: 'uppercase',
                color: 'var(--warning-fg)',
                marginBottom: 3,
              }}
            >
              Reminder
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', lineHeight: 1.4 }}>
              Work from home is at your manager's discretion
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.5, marginTop: 2 }}>
              It's for genuine needs and isn't a standard entitlement. Out on business (external
              meetings, conferences, training) only needs logging. Time away from the office during
              working hours without a record here counts as unrecorded absence.
            </div>
          </div>
        </div>
        <div style={tabListStyle} role="tablist" aria-label="Work from home">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'request'}
            style={{ ...tabButtonStyle, ...(tab === 'request' ? tabButtonActiveStyle : {}) }}
            onClick={() => setTab('request')}
          >
            New Request
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'mine'}
            style={{ ...tabButtonStyle, ...(tab === 'mine' ? tabButtonActiveStyle : {}) }}
            onClick={() => setTab('mine')}
          >
            My Requests
          </button>
          {isApprover && (
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'approvals'}
              style={{ ...tabButtonStyle, ...(tab === 'approvals' ? tabButtonActiveStyle : {}) }}
              onClick={() => setTab('approvals')}
            >
              For Approvals
              <TabCount n={pendingList.total} />
            </button>
          )}
        </div>

      {/* ── Apply ─────────────────────────────────────────────────────────── */}
      {tab === 'request' && (
      <Card padding={18}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Type picker — the prototype's "What kind of day is it?" cards. */}
          <Field label="What kind of day is it?" required>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10 }}>
              {(
                [
                  {
                    key: 'wfh' as const,
                    title: 'Work from home',
                    sub: "Needs your manager's approval",
                    icon: 'home' as IconName,
                  },
                  {
                    key: 'business' as const,
                    title: 'Out on business',
                    sub: 'External meeting, conference, training. Notify only',
                    icon: 'work' as IconName,
                  },
                ]
              ).map((card) => {
                const active = reqType === card.key
                return (
                  <button
                    key={card.key}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setReqType(card.key)}
                    style={{
                      display: 'flex',
                      gap: 12,
                      alignItems: 'flex-start',
                      textAlign: 'left',
                      padding: 14,
                      borderRadius: 10,
                      cursor: 'pointer',
                      border: `2px solid ${active ? 'var(--steel-blue)' : 'var(--border)'}`,
                      background: active ? 'var(--baby-blue-tint)' : 'var(--surface)',
                      color: 'var(--text)',
                      fontFamily: 'inherit',
                    }}
                  >
                    <Icon
                      name={card.icon}
                      size={22}
                      color="var(--steel-blue)"
                      aria-hidden="true"
                    />
                    <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <span style={{ fontWeight: 600 }}>{card.title}</span>
                      <span style={{ fontSize: 12.5, color: 'var(--text-2)' }}>{card.sub}</span>
                    </span>
                  </button>
                )
              })}
            </div>
          </Field>

          <Field label="Dates" required>
            <div ref={datesRef} style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={() => {
                  // Opening onto an existing range → next click is a fresh
                  // re-pick, not a range extension.
                  if (!datesOpen && from !== '') setDatesReopenFreshPick(true)
                  setDatesOpen((o) => !o)
                }}
                aria-haspopup="dialog"
                aria-expanded={datesOpen}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '11px 14px',
                  minHeight: 44,
                  background: 'var(--surface)',
                  border: `1px solid ${datesOpen ? 'var(--steel-blue)' : 'var(--border-strong)'}`,
                  boxShadow: datesOpen ? '0 0 0 3px var(--baby-blue-tint)' : 'none',
                  borderRadius: 10,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: 13.5,
                  color: 'var(--text)',
                  textAlign: 'left',
                  transition: 'border-color .12s, box-shadow .12s',
                }}
              >
                <Icon name="calendar_month" size={18} color="var(--text-3)" aria-hidden="true" />
                {datesSummary(from, to) ? (
                  <span style={{ fontWeight: 500 }}>
                    {datesSummary(from, to)}
                    {daysLabel ? (
                      <>
                        {' · '}
                        <strong style={{ fontWeight: 700 }}>{daysLabel}</strong>
                      </>
                    ) : null}
                  </span>
                ) : (
                  <span style={{ fontWeight: 500, color: 'var(--text-3)' }}>Set dates</span>
                )}
                <span
                  style={{
                    marginLeft: 'auto',
                    color: 'var(--text-3)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    transition: 'transform .15s',
                    transform: datesOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                >
                  <Icon name="expand_more" size={16} aria-hidden="true" />
                </span>
              </button>

              {datesOpen && (
                <FadeIn rise={4} duration="fast">
                  <div
                    role="group"
                    aria-label="Pick dates"
                    style={{
                      marginTop: 8,
                      background: 'var(--surface)',
                      border: '1px solid var(--border)',
                      borderRadius: 12,
                      padding: 12,
                    }}
                  >
                    <RangeCalendar
                      from={from}
                      to={to}
                      onChange={handleRange}
                      publicHolidays={[]}
                      min={minDate}
                      oneClickSingleDay
                      mobile={!isDesktop}
                    />
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        gap: 8,
                        marginTop: 8,
                      }}
                    >
                      <Button
                        type="button"
                        variant="tertiary"
                        size="sm"
                        disabled={from === ''}
                        onClick={() => {
                          setFrom('')
                          setTo('')
                          setHalfDays({})
                          setDatesReopenFreshPick(false)
                        }}
                      >
                        Clear
                      </Button>
                      <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        onClick={() => setDatesOpen(false)}
                      >
                        Done
                      </Button>
                    </div>
                  </div>
                </FadeIn>
              )}
            </div>
          </Field>

          <div style={{ fontSize: 12.5, color: 'var(--text-2)', marginTop: -6 }}>
            You can backdate up to 5 working days (back to {shortDate(minDate)}). Anything older
            goes through HR.
          </div>

          {/* Single-day requests pick a part of day; multi-day WFH picks
              per-date halfs on the calendar view below. */}
          {hasRange && singleDay && !isBusiness && (
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                Part of day
              </span>
              {(
                [
                  ['full', 'Full day'],
                  ['am', 'Morning'],
                  ['pm', 'Afternoon'],
                ] as const
              ).map(([key, label]) => {
                const active = portion === key
                return (
                  <button
                    key={key}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setPortion(key)}
                    style={{
                      minHeight: 36,
                      padding: '0 16px',
                      borderRadius: 999,
                      cursor: 'pointer',
                      border: `1px solid ${active ? 'var(--steel-blue)' : 'var(--border-strong)'}`,
                      background: active ? 'var(--steel-blue)' : 'var(--surface)',
                      color: active ? 'var(--surface)' : 'var(--text-2)',
                      fontWeight: 500,
                      fontFamily: 'inherit',
                      fontSize: 13,
                    }}
                  >
                    {label}
                  </button>
                )
              })}
            </div>
          )}

          {hasRange && !singleDay && !isBusiness && !datesOpen && (
            <HalfDayPicker
              startDate={from}
              endDate={to}
              publicHolidays={[]}
              value={halfDays}
              onChange={setHalfDays}
            />
          )}

          {isBusiness ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                <Field label="Type" required>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value as BusinessCategory)}
                    style={{
                      ...textareaStyle,
                      resize: 'none',
                      minHeight: 42,
                      padding: '8px 12px',
                    }}
                  >
                    {BUSINESS_CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Where" required>
                  <input
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="e.g. Client office, Bangsar"
                    style={{ ...textareaStyle, minHeight: 42, padding: '8px 12px' }}
                  />
                </Field>
              </div>
              <Field label="What it's for" required>
                <input
                  type="text"
                  value={purpose}
                  onChange={(e) => setPurpose(e.target.value)}
                  placeholder="e.g. Requirements workshop with the client team"
                  style={{ ...textareaStyle, minHeight: 42, padding: '8px 12px' }}
                />
              </Field>
            </>
          ) : (
            <Field label="Reason" required htmlFor="wfh-reason">
              <textarea
                id="wfh-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder="e.g. Contractor at home for aircon repair, online and reachable all day"
                style={textareaStyle}
              />
            </Field>
          )}

          <Field label="Attachment (optional)">
            <StagedDocumentPicker
              files={stagedFiles}
              onChange={setStagedFiles}
              disabled={createMut.isPending}
            />
          </Field>

          <div
            style={{
              fontSize: 13,
              color: 'var(--info-fg, var(--steel-blue))',
              background: 'var(--baby-blue-tint, var(--surface-2))',
              borderRadius: 8,
              padding: '10px 14px',
            }}
          >
            {isBusiness
              ? 'No approval needed. Your manager and HR are notified, and it shows on the HR attendance register.'
              : 'Goes to your manager for approval. Please submit by the end of the previous working day where you can.'}
          </div>

          {/* Prototype's long-range note: regular patterns go through HR. */}
          {!isBusiness && hasRange && workingDays.length > 2 && (
            <div
              style={{
                fontSize: 13,
                color: 'var(--warning-fg)',
                background: 'var(--warning-bg)',
                borderRadius: 8,
                padding: '10px 14px',
              }}
            >
              <strong>More than 2 working days.</strong> For a regular pattern, like every
              Friday, apply to HR for a flexible working arrangement instead.
            </div>
          )}

          <div>
            <Button
              variant="primary"
              disabled={!canSubmit}
              loading={createMut.isPending}
              disabledReason={
                isBusiness
                  ? 'Pick your dates and fill in where you will be and what it is for'
                  : 'Pick your dates and enter a reason'
              }
              onClick={() => createMut.mutate()}
            >
              {isBusiness ? 'Log out on business' : 'Send for approval'}
            </Button>
          </div>
        </div>
      </Card>
      )}

      {/* ── My requests ───────────────────────────────────────────────────── */}
      {tab === 'mine' && (
      <Card padding={18}>
        {mineList.isLoading ? (
          <ThinkingDots />
        ) : myRows.length === 0 ? (
          <EmptyState icon="event_available" text="No work-location requests yet." />
        ) : (
          <ul style={listStyle}>
            {myRows.map((r, i) => (
              <li key={r.id} style={rowStyle(i === 0)}>
                <div style={{ width: 96, flexShrink: 0 }}>
                  <Badge variant={STATUS_BADGE[r.status].variant}>
                    {STATUS_BADGE[r.status].label}
                  </Badge>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 6,
                      alignItems: 'center',
                      marginBottom: 2,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 11.5,
                        fontWeight: 700,
                        color: 'var(--steel-blue)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                      }}
                    >
                      {TYPE_LABEL[r.requestType]}
                    </span>
                    <FlagChip flag={r.timingFlag} />
                  </div>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)' }}>
                    {dateRangeLabel(r)}
                    <span style={{ fontWeight: 400, color: 'var(--text-2)' }}>
                      {' · '}
                      {daysText(requestDays(r))}
                    </span>
                  </div>
                  <div style={{ fontSize: 12.5, color: 'var(--text-2)' }}>
                    {r.requestType === 'business'
                      ? [r.category, r.location, r.purpose].filter(Boolean).join(' · ')
                      : `“${r.reason}”`}
                  </div>
                    <DocLinks req={r} onPreview={setPreview} />
                  {r.status === 'rejected' && r.rejectReason && (
                    <div style={{ fontSize: 12.5, color: 'var(--warning-fg)' }}>
                      Manager's reason: {r.rejectReason}
                    </div>
                  )}
                </div>
                {(r.status === 'pending' ||
                  (r.status === 'approved' && r.startDate > today) ||
                  (r.status === 'logged' && r.startDate > today)) && (
                  <Button
                    variant="tertiary"
                    size="sm"
                    loading={cancelMut.isPending && cancelMut.variables === r.id}
                    onClick={() => cancelMut.mutate(r.id)}
                  >
                    Cancel
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
        <Pager list={mineList} />
      </Card>
      )}

      {/* ── Approver inbox ────────────────────────────────────────────────── */}
      {isApprover && tab === 'approvals' && (
        <>
          {/* Your team today — the prototype's 4-bucket strip. */}
          {approvalsSummary.data && (
            <section aria-label="Your team today" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <h3 style={sectionLabel}>Your team today · {format(new Date(), 'EEE d MMM')}</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
                {approvalsSummary.data.groups.map((g) => (
                  <Card key={g.key} padding={12}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span
                        aria-hidden
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background:
                            g.key === 'office'
                              ? 'var(--text-3)'
                              : g.key === 'wfh'
                                ? 'var(--steel-blue)'
                                : g.key === 'business'
                                  ? 'var(--success-fg, green)'
                                  : 'var(--warning-fg)',
                          flexShrink: 0,
                        }}
                      />
                      <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-2)', flex: 1 }}>
                        {g.label}
                      </span>
                      <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>
                        {g.count}
                      </span>
                    </div>
                    {g.people.length === 0 ? (
                      <div style={{ fontSize: 13, color: 'var(--text-3)', marginTop: 6 }}>None</div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 6 }}>
                        {g.people.map((p) => (
                          <span key={p} style={{ fontSize: 13, color: 'var(--text)' }}>
                            {p}
                          </span>
                        ))}
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            </section>
          )}

          {/* ── OVERDUE · DATE HAS PASSED — the prototype's separate warning
              card for requests whose date has passed with no decision. ── */}
          {overdueList.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <h3 style={{ ...sectionLabel, color: 'var(--warning-fg)' }}>
                Overdue · date has passed
              </h3>
              <Card
                padding="0 18px"
                style={{ border: '1px solid var(--warning-line)' }}
              >
                <ul style={listStyle}>
                  {overdueList.map((p, i) => {
                    const r = p.request
                    const daysPassed = Math.max(
                      0,
                      Math.round(
                        (new Date(today).getTime() - new Date(r.endDate).getTime()) / 86400000,
                      ),
                    )
                    return (
                      <li key={r.id} style={rowStyle(i === 0)}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 8,
                              alignItems: 'center',
                              fontSize: 13.5,
                              color: 'var(--text)',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}>
                              {r.employeeName ?? 'Unknown employee'}
                            </span>
                            <span style={{ fontWeight: 400, color: 'var(--text-2)' }}>
                              {dateRangeLabel(r)} · {daysText(requestDays(r))}
                            </span>
                            <FlagChip flag={r.timingFlag} />
                          </div>
                          <div style={{ fontSize: 12.5, color: 'var(--text-2)' }}>
                            “{r.reason}”
                          </div>
                          <div style={{ fontSize: 12.5, color: 'var(--warning-fg)' }}>
                            Date passed {daysPassed} day{daysPassed === 1 ? '' : 's'} ago with no
                            decision. Shows as Unreviewed in the HR register until decided.
                            Approve after the fact if the day was actually worked.
                          </div>
                          <DocLinks req={r} onPreview={setPreview} />
                        </div>
                        <Button
                          variant="primary"
                          size="sm"
                          loading={
                            decideMut.isPending &&
                            decideMut.variables?.id === r.id &&
                            decideMut.variables?.decision === 'approve'
                          }
                          onClick={() => decideMut.mutate({ id: r.id, decision: 'approve' })}
                        >
                          Approve
                        </Button>
                        <Button
                          variant="tertiary"
                          size="sm"
                          onClick={() => {
                            setRejecting(r)
                            setRejectReason('')
                          }}
                        >
                          Reject
                        </Button>
                      </li>
                    )
                  })}
                </ul>
              </Card>
            </div>
          )}

          {/* ── PENDING (upcoming requests, in date order) ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <h3 style={sectionLabel}>Pending</h3>

            <Card padding={18}>
            {pendingList.isLoading ? (
              <ThinkingDots />
            ) : currentPending.length === 0 ? (
              overdueList.length > 0 ? (
                <EmptyState icon="inbox" text="Nothing upcoming — only overdue requests above." />
              ) : (
                <EmptyState icon="inbox" text="Nothing waiting on you." />
              )
            ) : (
              <ul style={listStyle}>
                {currentPending.map((p, i) => {
                  const r = p.request
                  const focused = r.id === highlightId
                  return (
                  <li
                    key={r.id}
                    ref={focused ? focusRowRef : undefined}
                    style={{
                      ...rowStyle(i === 0),
                      ...(focused
                        ? {
                            background: 'var(--baby-blue-tint, var(--surface-2))',
                            boxShadow: 'inset 3px 0 0 var(--steel-blue)',
                            borderRadius: 8,
                            padding: '9px 8px',
                            margin: '0 -8px',
                          }
                        : {}),
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 8,
                          alignItems: 'center',
                          fontSize: 13.5,
                          color: 'var(--text)',
                        }}
                      >
                        <span style={{ fontWeight: 600 }}>
                          {r.employeeName ?? 'Unknown employee'}
                        </span>
                        <span style={{ fontWeight: 400, color: 'var(--text-2)' }}>
                          {dateRangeLabel(r)} · {daysText(requestDays(r))}
                        </span>
                        <FlagChip flag={r.timingFlag} />
                      </div>
                      <div style={{ fontSize: 12.5, color: 'var(--text-2)' }}>“{r.reason}”</div>
                      <div style={{ fontSize: 12.5, color: 'var(--text-2)' }}>
                        WFH this month:{' '}
                        <strong style={{ color: 'var(--text)' }}>
                          {Number.isInteger(p.usage.wfhDaysThisMonth)
                            ? p.usage.wfhDaysThisMonth
                            : p.usage.wfhDaysThisMonth.toFixed(1)}
                        </strong>{' '}
                        · this quarter:{' '}
                        <strong style={{ color: 'var(--text)' }}>
                          {Number.isInteger(p.usage.wfhDaysThisQuarter)
                            ? p.usage.wfhDaysThisQuarter
                            : p.usage.wfhDaysThisQuarter.toFixed(1)}
                        </strong>
                      </div>
                      {p.notes.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                          {p.notes.map((n) => (
                            <span
                              key={n.text}
                              style={{
                                fontSize: 11.5,
                                color: 'var(--warning-fg)',
                                background: 'var(--warning-bg)',
                                borderRadius: 6,
                                padding: '3px 8px',
                              }}
                            >
                              {n.text}
                            </span>
                          ))}
                        </div>
                      )}
                    <DocLinks req={r} onPreview={setPreview} />
                    </div>
                    <Button
                      variant="primary"
                      size="sm"
                      loading={
                        decideMut.isPending &&
                        decideMut.variables?.id === r.id &&
                        decideMut.variables?.decision === 'approve'
                      }
                      onClick={() => decideMut.mutate({ id: r.id, decision: 'approve' })}
                    >
                      Approve
                    </Button>
                    <Button
                      variant="tertiary"
                      size="sm"
                      onClick={() => {
                        setRejecting(r)
                        setRejectReason('')
                      }}
                    >
                      Reject
                    </Button>
                  </li>
                  )
                })}
              </ul>
            )}
            <Pager list={pendingList} />
            </Card>
          </div>

          {/* ── OUT ON BUSINESS · FOR YOUR INFORMATION ── */}
          {approvalsSummary.data && approvalsSummary.data.businessFyi.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <h3 style={sectionLabel}>Out on business · for your information</h3>
              <Card padding={18}>
                <ul style={listStyle}>
                  {approvalsSummary.data.businessFyi.map((r, i) => (
                    <li key={r.id} style={rowStyle(i === 0)}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13.5, color: 'var(--text)' }}>
                          <span style={{ fontWeight: 600 }}>
                            {r.employeeName ?? 'Unknown employee'}
                          </span>
                          <span style={{ fontWeight: 400, color: 'var(--text-2)' }}>
                            {' · '}
                            {dateRangeLabel(r)} · {daysText(requestDays(r))}
                          </span>
                        </div>
                        <div style={{ fontSize: 12.5, color: 'var(--text-2)' }}>
                          {[r.category, r.location, r.purpose].filter(Boolean).join(' · ')}
                        </div>
                      </div>
                      {/* Notify-only — deliberately no Approve/Reject buttons. */}
                    </li>
                  ))}
                </ul>
              </Card>
            </div>
          )}

          {/* ── REVIEWED — the approver's past decisions ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <h3 style={sectionLabel}>Reviewed</h3>
            <Card padding={18}>
            {reviewedList.isLoading ? (
              <ThinkingDots />
            ) : reviewedRows.length === 0 ? (
              <EmptyState icon="history" text="No past decisions yet." />
            ) : (
                <ul style={listStyle}>
                  {reviewedRows.map((r, i) => (
                    <li key={r.id} style={rowStyle(i === 0)}>
                      <div style={{ width: 96, flexShrink: 0 }}>
                        <Badge variant={STATUS_BADGE[r.status].variant}>
                          {STATUS_BADGE[r.status].label}
                        </Badge>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13.5, color: 'var(--text)' }}>
                          <span style={{ fontWeight: 600 }}>
                            {r.employeeName ?? 'Unknown employee'}
                          </span>
                          <span style={{ fontWeight: 400, color: 'var(--text-2)' }}>
                            {' · '}
                            {dateRangeLabel(r)} · {daysText(requestDays(r))}
                          </span>
                        </div>
                        <div style={{ fontSize: 12.5, color: 'var(--text-2)' }}>“{r.reason}”</div>
                    <DocLinks req={r} onPreview={setPreview} />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <Pager list={reviewedList} />
              </Card>
            </div>
        </>
      )}
      </div>

      {/* ── Document preview modal ─────────────────────────────────────────── */}
      <Modal
        open={preview !== null}
        onClose={() => setPreview(null)}
        title={preview?.doc.filename ?? 'Document'}
        maxWidth="min(960px, 92vw)"
      >
        {preview !== null ? <WfhDocumentPreview preview={preview} /> : null}
      </Modal>

      {/* ── Reject modal ──────────────────────────────────────────────────── */}
      {rejecting != null && (
        <Modal open onClose={() => setRejecting(null)} title="Reject WFH request">
          <div style={{ display: 'grid', gap: 16, minWidth: 320 }}>
            <p style={mutedText}>
              {rejecting.employeeName ?? 'Unknown employee'} · {dateRangeLabel(rejecting)} —{' '}
              {rejecting.reason}
            </p>
            <Field label="Reason for rejection" required htmlFor="wfh-reject-reason">
              <textarea
                id="wfh-reject-reason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                autoFocus
                style={textareaStyle}
              />
            </Field>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="tertiary" onClick={() => setRejecting(null)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={rejectReason.trim().length === 0}
                loading={decideMut.isPending && decideMut.variables?.decision === 'reject'}
                onClick={() =>
                  decideMut.mutate({
                    id: rejecting.id,
                    decision: 'reject',
                    rejectReason: rejectReason.trim(),
                  })
                }
              >
                Reject request
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
